import React, {useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import AppDialog from 'src/components-new/feedback/AppDialog'
import ActivityAckDialogNew from 'src/components-new/feedback/ActivityAckDialogNew'
import ActivityDocumentsTabNew from 'src/components-new/feedback/ActivityDocumentsTabNew'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import Card from 'src/components-new/layout/Card'
import TabBar from 'src/components-new/layout/TabBar'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import ActivityAckIcon from 'src/components-new/primitives/ActivityAckIcon'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  ACTIVITY_STATES,
  buildQualificationsById,
  buildSubactivitiesView,
  computeActivityEndDate,
  extractActivityDescription,
  getActivityCompletionDate,
  getFeedbackTypeCodes,
  resolveQualificationNames,
  shouldShowBeneficiary,
} from './activityDetailUtils'
import {isAcknowledgeableActivity, isActivityAcknowledged, PatientAcknowledgement} from './activityAckUtils'

interface ActivityDetailDialogNewProps {
  activity: Record<string, any>
  open: boolean
  onClose: () => void
  /** Adds a "Documenti" tab alongside the details — opt-in per caller (only the Agenda for now). */
  showDocuments?: boolean
  /**
   * Called once the beneficiary confirms the "Presa visione". The "Presa visione"
   * icon/button only show up on the documents-enabled variant (the Agenda).
   */
  onAcknowledged?: (acknowledgement: PatientAcknowledgement) => void
}

const DETAIL_TABS = [
  {key: 'details', label: 'Dettagli'},
  {key: 'documents', label: 'Documenti'},
]

const DATE_FORMAT = 'ddd DD MMM YYYY HH:mm'
const LOADING_LABEL = 'Caricamento…'

const FIELD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
  gap: 2,
} as const

const SECTION_TITLE_SX = {
  color: NEUTRAL_SCALE[900],
  fontWeight: 600,
  fontSize: 16,
  mb: 1.5,
} as const

const GROUP_LABEL_SX = {
  color: NEUTRAL_SCALE[700],
  fontWeight: 600,
  fontSize: 13,
  mb: 1,
} as const

export default function ActivityDetailDialogNew({activity, open, onClose, showDocuments = false, onAcknowledged}: ActivityDetailDialogNewProps): JSX.Element {
  const loggedUserId = useAppSelector((state) => state.userReducer.data?._id)
  const toast = useToast()

  const [activeTab, setActiveTab] = useState<string>('details')
  const [operator, setOperator] = useState<Record<string, any> | undefined>()
  const [qualificationsById, setQualificationsById] = useState<Record<string, string>>({})
  const [loadingOperator, setLoadingOperator] = useState(false)
  const [subactivityInstances, setSubactivityInstances] = useState<Record<string, any>[]>([])
  const [feedbackConfigByType, setFeedbackConfigByType] = useState<Record<string, any[]>>({})
  const [loadingSubactivities, setLoadingSubactivities] = useState(false)
  const [isAckDialogOpen, setIsAckDialogOpen] = useState(false)

  // Back to the "Dettagli" tab whenever the dialog (re)opens, so switching
  // between activities never leaves "Documenti" selected for the new one.
  useEffect(() => {
    if (open) setActiveTab('details')
  }, [open, activity?._id])

  // Fetches the responsible's profile (+ the qualifications catalog to
  // resolve their qualification ids) and the activity's sub-activities
  // (+ the feedback-labels catalog needed to render their params) whenever
  // the dialog opens for a (possibly new) activity. Guarded so we don't call
  // out when the activity carries neither reference (e.g. the doctor-profile
  // activities tab, which uses appointeeId instead).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setOperator(undefined)
    setSubactivityInstances([])
    setFeedbackConfigByType({})

    if (activity?.responsibleId) {
      setLoadingOperator(true)
      Promise.all([
        apiClient.getUserById(activity.responsibleId, () => {}),
        apiClient.getQualifications(() => {}),
      ]).then(([user, qualifications]: [any, any]) => {
        if (cancelled) return
        setOperator(user)
        setQualificationsById(buildQualificationsById(qualifications))
      }).finally(() => { if (!cancelled) setLoadingOperator(false) })
    }

    if (activity?.subactivityIds?.length > 0) {
      setLoadingSubactivities(true)
      apiClient.getSubactivityInstances(activity._id, () => {})
        .then(async (instances: any[]) => {
          if (cancelled) return
          setSubactivityInstances(instances ?? [])
          // Only fetch feedback labels for the types actually rendered with
          // params — avoids calls for excluded/no-param sub-activities.
          const typeCodes = getFeedbackTypeCodes(instances)
          const entries = await Promise.all(
            typeCodes.map(async (typeCode) => [typeCode, await apiClient.getFeedbackConfig(typeCode, () => {})] as const)
          )
          if (!cancelled) setFeedbackConfigByType(Object.fromEntries(entries))
        })
        .finally(() => { if (!cancelled) setLoadingSubactivities(false) })
    }

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?._id])

  const activityState = ACTIVITY_STATES.find(s => s.value === activity.state)

  const showAcknowledgement = showDocuments && isAcknowledgeableActivity(activity, loggedUserId)
  const isAcknowledged = isActivityAcknowledged(activity)

  const showBeneficiary = shouldShowBeneficiary(loggedUserId, activity)
  const beneficiaryName = activity.beneficiary
    ? `${activity.beneficiary.firstName} ${activity.beneficiary.lastName}`
    : undefined

  const formattedStartDate = activity.plannedDate
    ? moment(activity.plannedDate).format(DATE_FORMAT)
    : undefined

  const endDateIso = computeActivityEndDate(activity.plannedDate, activity.activityReport?.activityDurationMinutes)
  const formattedEndDate = endDateIso ? moment(endDateIso).format(DATE_FORMAT) : undefined

  const durationLabel = activity.activityReport?.activityDurationMinutes != null
    ? String(activity.activityReport.activityDurationMinutes)
    : undefined

  const operatorName = operator ? `${operator.firstName ?? ''} ${operator.lastName ?? ''}`.trim() : undefined
  const qualificationLabel = resolveQualificationNames(operator?.qualifications, qualificationsById)

  const activityDescription = extractActivityDescription(subactivityInstances)
  const subactivities = buildSubactivitiesView(subactivityInstances, feedbackConfigByType)

  const isVirtualMeeting = activity.type === 'VIRTUAL_MEETING'
  const showMeetingPlace = !isVirtualMeeting && Boolean(activity.place)
  const showOperatorSection = Boolean(activity?.responsibleId)

  const handleOpenLink = () => {
    if (!activity.linkTeleconsultation) return
    window.open(activity.linkTeleconsultation, '_blank', 'noopener,noreferrer')
  }

  const handleCopyLink = async () => {
    if (!activity.linkTeleconsultation) return
    await navigator.clipboard.writeText(activity.linkTeleconsultation)
    toast.success('Link copiato negli appunti.')
  }

  const detailsContent = (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <Box>
        <Typography variant="h3" sx={SECTION_TITLE_SX}>Dettagli attività</Typography>
        <Box sx={FIELD_GRID_SX}>
          <ReadOnlyField label="Codice attività" value={activity._id} />
          {showBeneficiary && <ReadOnlyField label="Paziente" value={beneficiaryName} />}
          <ReadOnlyField label="Data ora inizio" value={formattedStartDate} />
          <ReadOnlyField label="Data ora fine" value={formattedEndDate} />
          {!isVirtualMeeting && (
            <ReadOnlyField label="Durata (minuti)" value={durationLabel} />
          )}
          {activity.linkTeleconsultation && (
            <Box sx={{gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-end', gap: 1}}>
              <Box sx={{flexGrow: 1, minWidth: 0}}>
                <TextFieldNew
                  disabled
                  fullWidth
                  label="Link per il collegamento"
                  onChange={() => undefined}
                  value={activity.linkTeleconsultation}
                />
              </Box>
              <ActionIconButton
                onClick={handleOpenLink}
                tooltip="Apri il link del videoconsulto"
                variant="videocam"
              />
              <ActionIconButton
                onClick={handleCopyLink}
                tooltip="Copia il link"
                variant="copy"
              />
            </Box>
          )}
        </Box>
      </Box>

      {showOperatorSection && (
        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Operatore</Typography>
          <Box sx={FIELD_GRID_SX}>
            <ReadOnlyField label="Operatore" value={loadingOperator ? LOADING_LABEL : operatorName} />
            <ReadOnlyField label="Qualifica" value={loadingOperator ? LOADING_LABEL : qualificationLabel} />
          </Box>
        </Box>
      )}

      {(loadingSubactivities || subactivities.length > 0) && (
        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Compiti</Typography>
          {loadingSubactivities ? (
            <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500]}}>{LOADING_LABEL}</Typography>
          ) : (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
              {subactivities.map((subactivity) => (
                <Card
                  key={subactivity.id}
                  title={subactivity.title}
                  action={(
                    <StatusPill
                      label={subactivity.isOk ? 'Avvenuto' : 'Non avvenuto'}
                      color={subactivity.isOk ? 'success' : 'error'}
                    />
                  )}
                >
                  {(subactivity.lines.length > 0 || subactivity.groups.length > 0) && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                      {subactivity.lines.length > 0 && (
                        <Box sx={FIELD_GRID_SX}>
                          {subactivity.lines.map((line, index) => (
                            <ReadOnlyField key={index} label={line.label} value={line.value} />
                          ))}
                        </Box>
                      )}
                      {subactivity.groups.map((group, groupIndex) => (
                        <Box key={groupIndex}>
                          {group.label && (
                            <Typography sx={GROUP_LABEL_SX}>{group.label}</Typography>
                          )}
                          <Box sx={FIELD_GRID_SX}>
                            {group.lines.map((line, lineIndex) => (
                              <ReadOnlyField key={lineIndex} label={line.label} value={line.value} />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {(loadingSubactivities || activityDescription) && (
        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Descrizione attività</Typography>
          <ReadOnlyField value={loadingSubactivities ? LOADING_LABEL : activityDescription} fullWidth />
        </Box>
      )}

      {showMeetingPlace && (
        <Box>
          <Typography variant="h3" sx={SECTION_TITLE_SX}>Luogo incontro</Typography>
          <Box sx={FIELD_GRID_SX}>
            <ReadOnlyField label="Stato" value={activity.place?.country} />
            <ReadOnlyField label="Regione" value={activity.place?.region} />
            <ReadOnlyField label="Provincia" value={activity.place?.province} />
            <ReadOnlyField label="Città" value={activity.place?.city} />
            <ReadOnlyField label="Indirizzo" value={activity.place?.address} />
            <ReadOnlyField label="Civico" value={activity.place?.streetNumber} />
            <ReadOnlyField label="Codice Postale" value={activity.place?.postalCode} />
            <ReadOnlyField label="Località" value={activity.place?.locality} />
          </Box>
        </Box>
      )}
    </Box>
  )

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={activity?.title ?? 'Attività'}
      maxWidth="md"
      // Il tab "Documenti" può aprire altri modali sopra questo (il Drawer dei
      // Filtri, il FilePreviewDialog): senza disabilitare il focus trap di questo
      // Dialog, MUI riporta il focus qui e i controlli dei modali annidati
      // risultano non interagibili (vedi lo stesso fix in DocumentUploaderNew/FilePreviewDialog).
      disableEnforceFocus={showDocuments}
      titleAction={(showAcknowledgement || activityState) && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {showAcknowledgement && <ActivityAckIcon activity={activity} />}
          {activityState && <StatusPill label={activityState.label} color={activityState.color} />}
        </Box>
      )}
      actions={showAcknowledgement && !isAcknowledged ? (
        <Button onClick={() => setIsAckDialogOpen(true)}>Presa visione</Button>
      ) : undefined}
    >
      {showDocuments && (
        <Box sx={{borderBottom: '1px solid', borderColor: 'divider', mb: 2}}>
          <TabBar tabs={DETAIL_TABS} activeKey={activeTab} onChange={setActiveTab} />
        </Box>
      )}

      {!showDocuments || activeTab === 'details' ? detailsContent : (
        <ActivityDocumentsTabNew
          activityId={activity._id}
          beneficiaryId={activity.beneficiaryId}
          showActivitySheet={activity.state === 'COMPLETED' && Boolean(activity.beneficiaryId)}
          plannedDate={activity.plannedDate}
          completionDate={getActivityCompletionDate(activity.statesTransitions)}
        />
      )}

      {showAcknowledgement && isAckDialogOpen && (
        <ActivityAckDialogNew
          activityId={activity._id}
          open={isAckDialogOpen}
          onClose={() => setIsAckDialogOpen(false)}
          onConfirmed={(acknowledgement) => {
            setIsAckDialogOpen(false)
            onAcknowledged?.(acknowledgement)
          }}
        />
      )}
    </AppDialog>
  )
}
